import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconAxe, IconBuildingFactory2 } from "@tabler/icons-react";

import { AsiLogo } from "../components/AsiLogo";
import { saveSession, setDevBypass, type Session } from "../lib/auth";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";
// Cross-app URL for the Mill Grading platform (separate Vite server).
// Dev default matches apps/mill-web/vite.config.ts port 5180.
const MILL_APP_URL =
  import.meta.env.VITE_MILL_APP_URL ?? "http://localhost:5180";

/**
 * Sign-in · ASI RGCQ Intelligence Platform.
 *
 * The platform hosts two apps under one commercial plan:
 *   · Harvesting (this app · estate-web) — planning + Mandor + payroll
 *   · Mill Grading (mill-web) — W/B + Tier 1 + Tier 2 + Borang
 *
 * The picker below highlights the current app and links out to the other.
 * Which apps a user can enter is gated by their subscription bundle.
 *
 * Landing route auto-derives from device width — phones go to /mandor,
 * desktops go to /estate-land.
 */
export function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Landing route derives from device width — phones go to /mandor
  // (Field), desktops go to /estate-land (Office). Not user-selectable
  // now that the picker chooses between platform apps instead.
  const [workspace] = useState<"office" | "field">(() =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches
      ? "field"
      : "office",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${BASE_URL}/v1/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const body = (await res.json()) as
        | (Omit<Session, "workspace"> & { error?: undefined })
        | { error: { message: string } };
      if (!res.ok || "error" in body && body.error) {
        setError(
          "error" in body && body.error
            ? body.error.message
            : `Sign-in failed (${res.status})`,
        );
        return;
      }
      const ok = body as Omit<Session, "workspace">;
      saveSession({ ...ok, workspace });
      navigate(workspace === "field" ? "/mandor" : "/estate-land", {
        replace: true,
      });
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  const appBtn = (active: boolean) =>
    `flex flex-1 flex-col items-center gap-1 rounded-lg border px-3 py-3 text-xs transition ${
      active
        ? "border-brand-green bg-brand-green/10 text-brand-green-dark font-semibold"
        : "border-border text-text-secondary hover:bg-surface-1"
    }`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3">
          <AsiLogo variant="mark" size={72} />
          <div className="text-center">
            <div className="text-lg font-extrabold leading-tight text-brand-charcoal">
              ASI <span className="text-brand-green-dark">Builder</span>
            </div>
            <div className="text-[11px] font-medium text-brand-green-dark">
              AI Automation Agent
            </div>
            <div className="mt-2 text-[12px] font-semibold text-text-primary">
              Smart Harvesting
            </div>
            <div className="text-[11px] text-text-secondary">
              Sign in to your estate workspace
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 rounded-xl border border-border bg-surface-2 p-5 shadow-sm"
        >
          {/* Platform app picker · Harvesting (this app) vs Mill Grading */}
          <div>
            <div className="mb-1.5 text-xs font-medium">Platform app</div>
            <div className="flex gap-2">
              <button
                type="button"
                className={appBtn(true)}
                title="You are on the Harvesting app"
              >
                <IconAxe size={20} />
                Harvesting
                <span className="text-[10px] font-normal">
                  planning · Mandor · TPH · payroll
                </span>
              </button>
              <a
                href={MILL_APP_URL}
                className={appBtn(false)}
                title="Switch to the Mill Grading app"
              >
                <IconBuildingFactory2 size={20} />
                Mill Grading
                <span className="text-[10px] font-normal">
                  W/B · Tier 1 + Tier 2 · Borang
                </span>
              </a>
            </div>
            <div className="mt-1.5 text-[10px] text-text-secondary">
              Which apps you can enter is set by your subscription bundle.
            </div>
          </div>

          <label className="block text-xs">
            <span className="mb-1 block font-medium">Email</span>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
              placeholder="you@company.com"
            />
          </label>

          <label className="block text-xs">
            <span className="mb-1 block font-medium">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
              placeholder="••••••••"
            />
          </label>

          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-2.5 text-xs text-red-800">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-brand-green py-2.5 text-sm font-semibold text-white hover:bg-brand-green-dark transition-colors disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

          {import.meta.env.DEV && (
            <button
              type="button"
              onClick={() => {
                setDevBypass();
                navigate("/estate-land", { replace: true });
              }}
              className="w-full text-center text-[11px] text-text-secondary underline"
            >
              Continue in dev mode (no sign-in)
            </button>
          )}
        </form>

        <div className="mt-4 text-center text-[11px] text-text-secondary">
          One account works on both desktop and mobile browsers.
        </div>
      </div>
    </div>
  );
}
